import { PointerEvent as ReactPointerEvent, RefObject, useCallback, useEffect, useRef } from "react";

// transitions.dev "Card hover tilt" — pointer orchestration.
//
// Tilts a card in 3D toward the pointer and slides a soft light glare under
// the cursor. The pointer is tracked on an OUTER wrapper that never transforms
// (spread the returned handlers onto it) and the rotation is written onto the
// INNER card, which is the element that rotates. Tracking the rotating element
// itself lets its edges slip out from under the cursor near the borders and
// the hover flickers on and off.
//
// Contract with the card's CSS:
//   card  `transform` is written inline per move as
//         perspective(var(--tilt-perspective)) rotateX() rotateY(); the
//         stylesheet's resting transform must use the same three functions so
//         the return-to-flat interpolates pairwise instead of via matrices.
//   glare `transform: translate(x%, y%)` written inline per move.
//   [data-tilting]  short follow transition while the pointer moves
//   [data-hover]    fades the glare in
// Timing tunables (`--tilt-*`) are installed on `:root` in App.tsx.
//
// Project motion conventions:
//   - Compositor-only: per move, only the card's `transform` and the glare's
//     `transform` change. The glare is a sheet twice the card's size with its
//     gradients painted ONCE at the centre; translating the sheet under the
//     pointer replaces re-painting a gradient position, so its offset is
//     (pointer − 0.5) × 50% of the SHEET, not of the card.
//   - Transforms are written directly, not through `--tilt-rx`-style custom
//     properties as in the reference: custom properties inherit, so writing
//     one on the card would invalidate computed style for its entire subtree
//     on every pointermove. `transform` is not inherited.
//   - No React state on the hot path: everything is written to the DOM off refs.
//   - One-shot `will-change`: hinted on the first move of a hover, released
//     once the return-to-flat transition has finished.
//   - Reduced motion: bails before touching the DOM (the CSS side flattens too).
//   - Mouse only. The reference also drives the tilt from a touch drag via
//     `touch-action: none`, but that takes the scroll away from any finger that
//     lands on the card — a non-starter for a large card at the top of a page.

const reduceMotion =
  typeof window !== "undefined" && typeof window.matchMedia === "function"
    ? window.matchMedia("(prefers-reduced-motion: reduce)")
    : null;

/** Parse a CSS <time> custom property ("1000ms" | "1s") into milliseconds. */
const parseMs = (raw: string, fallback: number) => {
  const n = parseFloat(raw);
  if (!Number.isFinite(n)) {
    return fallback;
  }
  return raw.trim().endsWith("ms") ? n : n * 1000;
};

interface Params {
  /** The element that rotates. Lives inside the wrapper the handlers go on. */
  cardRef: RefObject<HTMLElement>;
  /** The glare sheet inside the card; promoted alongside it while hovered. */
  glareRef?: RefObject<HTMLElement>;
  /** Peak lean in degrees when the pointer sits on a card edge. 10–16° reads as subtle. */
  maxDeg?: number;
}

export const useCardTilt = ({ cardRef, glareRef, maxDeg = 14 }: Params) => {
  // Pending will-change release, armed when the pointer leaves.
  const releaseTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  const cancelRelease = () => {
    if (releaseTimer.current) {
      clearTimeout(releaseTimer.current);
      releaseTimer.current = null;
    }
  };

  const reset = useCallback(() => {
    const card = cardRef.current;
    // Nothing to undo unless a mouse hover actually started (touch never does).
    if (!card || card.dataset.hover === undefined) {
      return;
    }
    const glare = glareRef?.current;
    // Style read before the writes below so it doesn't force a recalc.
    const returnMs = parseMs(getComputedStyle(card).getPropertyValue("--tilt-return"), 1000);
    delete card.dataset.hover;
    delete card.dataset.tilting;
    // Clearing the inline transform hands the card back to the stylesheet's
    // flat rest value, which the return transition eases toward. The glare's
    // offset is left where it was so it fades out in place instead of jumping
    // to the centre first.
    card.style.transform = "";
    // Keep the layers hinted while the card eases back to flat and the glare
    // fades, then release them. Read from CSS so a retuned `--tilt-return`
    // can't outlive the hint.
    cancelRelease();
    releaseTimer.current = setTimeout(() => {
      card.style.willChange = "";
      if (glare) {
        glare.style.willChange = "";
      }
      releaseTimer.current = null;
    }, returnMs + 50);
  }, [cardRef, glareRef]);

  const onPointerMove = useCallback(
    (e: ReactPointerEvent<HTMLElement>) => {
      if (e.pointerType !== "mouse" || reduceMotion?.matches) {
        return;
      }
      const card = cardRef.current;
      if (!card) {
        return;
      }
      // One layout read per move, before any write; the writes below only
      // touch transform, so layout stays clean for the next read.
      const r = e.currentTarget.getBoundingClientRect();
      if (!r.width || !r.height) {
        return;
      }
      const px = Math.min(1, Math.max(0, (e.clientX - r.left) / r.width));
      const py = Math.min(1, Math.max(0, (e.clientY - r.top) / r.height));
      const glare = glareRef?.current;
      if (card.dataset.hover === undefined) {
        // First move of a hover: promote for the length of the interaction.
        cancelRelease();
        card.style.willChange = "transform";
        if (glare) {
          glare.style.willChange = "transform, opacity";
        }
        card.dataset.hover = "";
        card.dataset.tilting = "";
      }
      const ry = ((px - 0.5) * maxDeg).toFixed(2);
      const rx = ((0.5 - py) * maxDeg).toFixed(2);
      card.style.transform = `perspective(var(--tilt-perspective)) rotateX(${rx}deg) rotateY(${ry}deg)`;
      if (glare) {
        glare.style.transform = `translate(${((px - 0.5) * 50).toFixed(1)}%, ${((py - 0.5) * 50).toFixed(1)}%)`;
      }
    },
    [cardRef, glareRef, maxDeg]
  );

  // Don't let a pending release fire into an unmounted card.
  useEffect(() => cancelRelease, []);

  // Spread onto the flat wrapper. No pointerup: for a mouse that is a click,
  // and flattening the card on every click would jolt it before the next move.
  return { onPointerMove, onPointerLeave: reset, onPointerCancel: reset };
};
